import React, { useEffect, useMemo, useState } from "react";

// Import components
import Breadcrumbs from '../../components/Common/Breadcrumb';
import TableContainer from '../../components/Common/DataTableContainer';
import { Button } from "reactstrap";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "components/Modals/ConfirmationModal";
import { deleteSiteSurvey, deleteSiteSurveyRequest } from "store/actions";  // Replace with your delete action for Site Surveys
import { formatDateTimeToAmPm } from "helpers/dateFormat_helper";
import Cookies from "js-cookie";
import { MdDeleteOutline } from "react-icons/md";

const SiteSurveyTable = ({ List, loading, totalCount, pageIndex, pageSize, sorting, onPaginationChange, onSortingChange, isPagination = true }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [deleteId, setDeleteId] = useState(false);
  const [confirmAction, setConfirmAction] = useState(false);

  const permissions2 = JSON.parse(localStorage?.getItem('permissions')) || [];

  const hasDeletePermission = permissions2.some(item => item?.permissionName === "Delete SiteSurvey");


  const handleDelete = (SiteSurveyId) => {
    dispatch(deleteSiteSurvey(SiteSurveyId));
    setDeleteId('');
    setOpenModal(false);
    setConfirmAction(false);
  };

  useEffect(() => {
    if (deleteId && confirmAction) {
      handleDelete(deleteId);
    }
  }, [deleteId, confirmAction]);

  const handleNavigate = (sitesurveyId) => {
    Cookies.set('siteSurveyEndDate', localStorage?.getItem('siteSurveyEndDate'));
    Cookies.set('siteSurveyStartDate', localStorage?.getItem('siteSurveyStartDate'));
    Cookies.set('siteSurveySelectedTeam', localStorage?.getItem('siteSurveySelectedTeam'));
    Cookies.set('siteSurveySelectedStatus', localStorage?.getItem('siteSurveySelectedStatus'));
    Cookies.set('siteSurveySelectedSolution',localStorage.getItem('siteSurveySelectedSolution'));
    Cookies.set('siteSurveySebelUserId',localStorage.getItem('siteSurveySebelUserId'));
    Cookies.set('siteSurveyCreatedAt',localStorage.getItem('siteSurveyCreatedAt'));
    navigate(`/SiteSurveyDetails/${sitesurveyId}`);
  }

  const columns = useMemo(
    () => [
      {
        header: 'Site Survey Number',
        accessorKey: 'siteSurveyNumber',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const permissionNames = permissions2?.map(item => item?.permissionName) || [];

          const canClick =  permissionNames.includes("Add SiteSurvey") ||
                            permissionNames.includes("Site Survey Extension") ||
                            permissionNames.includes("Site Survey Details") ||
                            permissionNames.includes("Site Survey Cancellation") ||
                            permissionNames.includes("Site Survey Extension") ||
                            permissionNames.includes("Edit SiteSurvey");
          return canClick ? (
            <span
              title="View Site Survey"
              style={{ cursor: 'pointer', color: "rgb(4, 161, 182)" }}
              onClick={() => handleNavigate(row.original?.sitesurveyId)}
            >
              {row.original.siteSurveyNumber}
            </span>
          ) : (
            <span style={{ color: "#666" }}>
              {row.original.siteSurveyNumber}
            </span>
          );
          
        },
      },
      {
        header: 'Team',
        accessorKey: 'teamName',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Customer Name',
        accessorKey: 'customerNameEng',
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: 'Customer Phone Number',
        accessorKey: 'contactNumber1',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Created By',
        accessorKey: 'createdBy',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'Scheduled Start Date',
        accessorKey: 'scheduledStartDateTime',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <span>
            {formatDateTimeToAmPm(row.original.scheduledStartDateTime)}
          </span>
        ),
      },
      {
        header: 'Scheduled End Date',
        accessorKey: 'scheduledEndDateTime',
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <span>
            {formatDateTimeToAmPm(row.original.scheduledEndDateTime)}
          </span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'SiteSurveyStatus',
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ getValue }) => getValue() || 'N/A',
      },
      // Conditionally render Actions column based on permission
      ...(hasDeletePermission
        ? [
          {
            header: 'Actions',
            id: 'actions', // Unique id for this column
            enableSorting: false,
            cell: ({ row }) => {
              return (
                <div className="d-flex gap-2">
                  <Button
                    color="danger"
                    onClick={() => { setDeleteId(row?.original?.sitesurveyId); setOpenModal(true); }}
                    className="mr-2"
                  >
                    <MdDeleteOutline size={18} />
                  </Button>
                </div>
              );
            },
          },
        ]
        : []), // If no delete permission, return an empty array (no actions column)
    ],
    [hasDeletePermission] // Re-run this useMemo when the permission changes
  );

  // Meta title for the page
  document.title = "Site Surveys | Your Admin Dashboard";

  return (
    <>
      <ConfirmationModal
        okText={"Confirm"}
        onCancel={() => { setDeleteId(''); setOpenModal(false); }}
        onOk={() => { setConfirmAction(true); }}
        isVisible={openModal}
        title="Delete Site Survey"
        content="Are you sure you want to delete this Site Survey?"
      />
      <div className="">
        <div className="container-fluid">
          {/* <Breadcrumbs title="Tables" breadcrumbItem="Site Surveys" /> */}

          <TableContainer
            loading={loading}
            columns={columns}
            data={List || []}
            isGlobalFilter={false}
            manualPagination={true}
            manualSorting={true}
            manualFiltering={true}
            totalCount={List && List.length > 0 ? List[0]?.TotalCount : totalCount}
            pageCount={List && List.length > 0 ? Math.ceil(List[0]?.TotalCount / pageSize) : Math.ceil(totalCount / pageSize)}
            paginationState={{ pageIndex, pageSize }}
            sortingState={sorting}
            onPaginationChange={onPaginationChange}
            onSortingChange={onSortingChange}
            isPagination={isPagination}
            SearchPlaceholder="Search..."
            pagination="pagination"
            docName="Site Surveys"
            paginationWrapper='dataTables_paginate paging_simple_numbers'
            tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          />
        </div>
      </div>
    </>
  );
};

export default SiteSurveyTable;
